import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, Observable, Subject, takeUntil } from 'rxjs';
import { NotificationsService } from '@core/services/backend/common/service/notifications.service';
import { AuthService } from '@core/guard/auth-service';
import { tap, catchError } from 'rxjs/operators';
import { of } from 'rxjs';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { environment } from '@environments/environment';

export interface NotificationData {
    id: number;
    type: string;
    message: string;
    aux_id: number;
    read_it: boolean;
    user_id: number;
    priority?: string;
    category?: string;
    action_url?: string;
    expires_at?: string;
    metadata?: string;
    created_at?: string;
    updated_at?: string;
}

@Injectable({ providedIn: 'root' })
export class NotificationsManagerService implements OnDestroy {
    private readonly POLL_INTERVAL = 30000; // 30 segundos (fallback)
    private readonly destroying$ = new Subject<void>();
    private pollingInterval?: any;
    private eventSource?: EventSource;
    private readonly BASE_URL = environment.apiUrl; // URL base del backend
    private reconnectAttempts = 0;
    private readonly MAX_RECONNECT_ATTEMPTS = 5;
    private readonly RECONNECT_DELAY = 3000; // 3 segundos
    
    private notifications$ = new BehaviorSubject<NotificationData[]>([]);
    private unreadCount$ = new BehaviorSubject<number>(0);
    private isLoading$ = new BehaviorSubject<boolean>(false);
    private useSSE = true; // Flag para habilitar/deshabilitar SSE

    private get SSE_URL(): string {
        return `${this.BASE_URL}/api/notifications/stream`;
    }

    constructor(
        private notificationsService: NotificationsService,
        private authService: AuthService,
        private http: HttpClient
    ) {}

    /**
     * Inicia la conexión SSE o polling de notificaciones
     */
    startPolling(): void {
        if (this.useSSE) {
            this.startSSE();
        } else {
            this.startPollingFallback();
        }
    }

    /**
     * Inicia la conexión Server-Sent Events
     */
    private startSSE(): void {
        const userId = this.authService.UserId;
        if (!userId) {
            console.warn('No hay userId, usando polling fallback');
            this.startPollingFallback();
            return;
        }

        // Cerrar conexión anterior si existe
        this.stopSSE();

        // Obtener token de autenticación
        let token = this.authService.token;
        if (!token) {
            console.warn('No hay token, usando polling fallback');
            this.startPollingFallback();
            return;
        }

        // Limpiar comillas si el token las tiene (puede venir con comillas del localStorage)
        token = token.replace(/^["']|["']$/g, '').trim();

        // EventSource no soporta headers personalizados, así que pasamos el token como query param
        // El backend deberá validar el token desde query params o headers
        const url = `${this.SSE_URL}?token=${encodeURIComponent(token)}`;
        
        console.log('SSE: Intentando conectar a:', url.replace(/token=[^&]+/, 'token=***'));

        try {
            this.eventSource = new EventSource(url);

            this.eventSource.onopen = () => {
                console.log('SSE: Conexión establecida');
                this.reconnectAttempts = 0;
                this.isLoading$.next(false);
            };

            this.eventSource.onmessage = (event) => {
                try {
                    const data = JSON.parse(event.data);
                    this.handleSSEEvent(event.type || 'message', data);
                } catch (e) {
                    console.error('Error parseando evento SSE:', e);
                }
            };

            // Manejar eventos específicos
            this.eventSource.addEventListener('connected', (event: any) => {
                console.log('SSE: Conectado', event.data);
                this.isLoading$.next(false);
            });

            this.eventSource.addEventListener('initial', (event: any) => {
                try {
                    const notifications = JSON.parse(event.data);
                    this.processNotifications(notifications);
                } catch (e) {
                    console.error('Error procesando notificaciones iniciales:', e);
                }
            });

            this.eventSource.addEventListener('notification', (event: any) => {
                try {
                    const notification = JSON.parse(event.data);
                    this.handleNewNotification(notification);
                } catch (e) {
                    console.error('Error procesando nueva notificación:', e);
                }
            });

            this.eventSource.addEventListener('count', (event: any) => {
                try {
                    const data = JSON.parse(event.data);
                    this.unreadCount$.next(data.count || 0);
                } catch (e) {
                    console.error('Error procesando conteo:', e);
                }
            });

            this.eventSource.addEventListener('heartbeat', () => {
                // Mantener conexión viva
            });

            this.eventSource.addEventListener('error', (event: any) => {
                console.error('SSE: Error en conexión', event);
                this.handleSSEError();
            });

            this.eventSource.onerror = (error) => {
                console.error('SSE: Error general', error);
                this.handleSSEError();
            };

            // Cargar notificaciones iniciales mientras se establece SSE
            this.loadNotifications();
            this.loadUnreadCount();

        } catch (error) {
            console.error('Error iniciando SSE, usando polling fallback:', error);
            this.startPollingFallback();
        }
    }

    /**
     * Maneja eventos SSE
     */
    private handleSSEEvent(type: string, data: any): void {
        switch (type) {
            case 'connected':
                console.log('SSE conectado');
                break;
            case 'notification':
                this.handleNewNotification(data);
                break;
            case 'count':
                this.unreadCount$.next(data.count || 0);
                break;
            case 'initial':
                this.processNotifications(data);
                break;
        }
    }

    /**
     * Maneja errores de SSE y reconecta o cambia a polling
     */
    private handleSSEError(): void {
        this.isLoading$.next(false);
        
        if (this.reconnectAttempts < this.MAX_RECONNECT_ATTEMPTS) {
            this.reconnectAttempts++;
            console.log(`SSE: Reintentando conexión (${this.reconnectAttempts}/${this.MAX_RECONNECT_ATTEMPTS})...`);
            
            setTimeout(() => {
                this.stopSSE();
                this.startSSE();
            }, this.RECONNECT_DELAY);
        } else {
            console.warn('SSE: Máximo de reintentos alcanzado, cambiando a polling');
            this.useSSE = false;
            this.stopSSE();
            this.startPollingFallback();
        }
    }

    /**
     * Maneja una nueva notificación recibida por SSE
     */
    private handleNewNotification(notification: any): void {
        const userId = this.authService.UserId;
        const nUserId = notification.user_id || notification.UserId || notification.userId;
        
        // Solo procesar notificaciones para el usuario actual
        if (String(nUserId) !== String(userId)) {
            return;
        }

        // Convertir a formato NotificationData
        const notificationData: NotificationData = {
            id: Number(notification.ID || notification.id) || 0,
            type: notification.type || notification.Type || '',
            message: notification.message || notification.Message || '',
            aux_id: Number(notification.aux_id || notification.AuxID || notification.auxId) || 0,
            read_it: notification.read_it || notification.ReadIt || notification.readIt || false,
            user_id: Number(notification.user_id || notification.UserId || notification.userId) || 0,
            priority: notification.priority || notification.Priority || 'normal',
            category: notification.category || notification.Category || '',
            action_url: notification.action_url || notification.ActionURL || notification.actionUrl,
            expires_at: notification.expires_at || notification.ExpiresAt || notification.expiresAt,
            metadata: notification.metadata || notification.Metadata,
            created_at: notification.created_at || notification.CreatedAt || notification.createdAt,
            updated_at: notification.updated_at || notification.UpdatedAt || notification.updatedAt
        };

        // Solo agregar si no está leída
        if (!notificationData.read_it) {
            const currentNotifications = this.notifications$.value;
            // Evitar duplicados
            const exists = currentNotifications.some(n => n.id === notificationData.id);
            if (!exists) {
                this.notifications$.next([notificationData, ...currentNotifications]);
            }
        }
    }

    /**
     * Procesa un array de notificaciones
     */
    private processNotifications(notifications: any[]): void {
        const userId = this.authService.UserId;
        
        const processed = notifications
            .filter((n: any) => {
                const nUserId = n.user_id || n.UserId || n.userId;
                return String(nUserId) === String(userId);
            })
            .map((n: any): NotificationData => ({
                id: Number(n.ID || n.id) || 0,
                type: n.type || n.Type || '',
                message: n.message || n.Message || '',
                aux_id: Number(n.aux_id || n.AuxID || n.auxId) || 0,
                read_it: n.read_it || n.ReadIt || n.readIt || false,
                user_id: Number(n.user_id || n.UserId || n.userId) || 0,
                priority: n.priority || n.Priority || 'normal',
                category: n.category || n.Category || '',
                action_url: n.action_url || n.ActionURL || n.actionUrl,
                expires_at: n.expires_at || n.ExpiresAt || n.expiresAt,
                metadata: n.metadata || n.Metadata,
                created_at: n.created_at || n.CreatedAt || n.createdAt,
                updated_at: n.updated_at || n.UpdatedAt || n.updatedAt
            }))
            .filter(n => !n.read_it); // Solo no leídas
        
        this.notifications$.next(processed);
    }

    /**
     * Inicia polling como fallback
     */
    private startPollingFallback(): void {
        console.log('Usando polling como fallback');
        // Cargar inmediatamente notificaciones y conteo
        this.loadNotifications();
        this.loadUnreadCount();
        
        // Configurar polling periódico
        this.pollingInterval = setInterval(() => {
            this.loadNotifications();
            this.loadUnreadCount();
        }, this.POLL_INTERVAL);
    }

    /**
     * Detiene la conexión SSE
     */
    private stopSSE(): void {
        if (this.eventSource) {
            this.eventSource.close();
            this.eventSource = undefined;
        }
    }

    /**
     * Detiene el polling de notificaciones
     */
    stopPolling(): void {
        this.stopSSE();
        if (this.pollingInterval) {
            clearInterval(this.pollingInterval);
            this.pollingInterval = undefined;
        }
    }

    /**
     * Carga las notificaciones desde el backend
     */
    loadNotifications(): void {
        const userId = this.authService.UserId;
        if (!userId) {
            return;
        }

        this.isLoading$.next(true);
        
        // Buscar solo no leídas por defecto
        const search = JSON.stringify({ 
            userId: userId, 
            readIt: false 
        });

        this.notificationsService.list(search, 0, 50, 'id DESC')
            .pipe(
                tap(() => this.isLoading$.next(false)),
                catchError(error => {
                    console.error('Error cargando notificaciones:', error);
                    this.isLoading$.next(false);
                    return of([]);
                })
            )
            .subscribe((response: any) => {
                let notifications: NotificationData[] = [];
                
                if (Array.isArray(response)) {
                    notifications = response;
                } else if (response?.data && Array.isArray(response.data)) {
                    notifications = response.data;
                } else if (response?.Data && Array.isArray(response.Data)) {
                    notifications = response.Data;
                }

                // Filtrar por usuario actual (por seguridad) y convertir tipos
                notifications = notifications
                    .filter((n: any) => {
                        const nUserId = n.user_id || n.UserId || n.userId;
                        return String(nUserId) === String(userId);
                    })
                    .map((n: any): NotificationData => ({
                        id: Number(n.ID || n.id) || 0,
                        type: n.type || n.Type || '',
                        message: n.message || n.Message || '',
                        aux_id: Number(n.aux_id || n.AuxID || n.auxId) || 0,
                        read_it: n.read_it || n.ReadIt || n.readIt || false,
                        user_id: Number(n.user_id || n.UserId || n.userId) || 0,
                        priority: n.priority || n.Priority || 'normal',
                        category: n.category || n.Category || '',
                        action_url: n.action_url || n.ActionURL || n.actionUrl,
                        expires_at: n.expires_at || n.ExpiresAt || n.expiresAt,
                        metadata: n.metadata || n.Metadata,
                        created_at: n.created_at || n.CreatedAt || n.createdAt,
                        updated_at: n.updated_at || n.UpdatedAt || n.updatedAt
                    }));
                
                this.notifications$.next(notifications);
                
                // Siempre obtener el conteo desde el backend para asegurar precisión
                this.loadUnreadCount();
            });
    }

    /**
     * Carga el conteo de notificaciones no leídas
     */
    loadUnreadCount(): void {
        this.notificationsService.getUnreadCount()
            .pipe(
                catchError(error => {
                    console.error('Error cargando conteo de notificaciones:', error);
                    return of({ count: 0 });
                })
            )
            .subscribe(result => {
                this.unreadCount$.next(result.count || 0);
            });
    }

    /**
     * Actualiza el conteo de no leídas basado en las notificaciones cargadas
     * NOTA: Este método ya no se usa. El conteo siempre se obtiene del backend mediante loadUnreadCount()
     */
    private updateUnreadCount(): void {
        // Ya no se usa - el conteo siempre viene del backend
        // Se mantiene por compatibilidad pero no hace nada
    }

    /**
     * Marca una notificación como leída
     */
    markAsRead(notificationId: number): Observable<any> {
        return this.notificationsService.update([{
            id: notificationId,
            read_it: true
        }]).pipe(
            tap(() => {
                // Actualizar localmente
                const notifications = this.notifications$.value.map(n => 
                    n.id === notificationId ? { ...n, read_it: true } : n
                );
                this.notifications$.next(notifications);
                // Recargar conteo desde el backend para asegurar precisión
                this.loadUnreadCount();
            })
        );
    }

    /**
     * Marca todas las notificaciones como leídas
     */
    markAllAsRead(): Observable<any> {
        const unreadNotifications = this.notifications$.value.filter(n => !n.read_it);
        if (unreadNotifications.length === 0) {
            return of(null);
        }
        
        const updates = unreadNotifications.map(n => ({
            id: n.id,
            read_it: true
        }));

        return this.notificationsService.update(updates).pipe(
            tap(() => {
                // Recargar notificaciones desde el backend para obtener solo las no leídas
                // Esto asegura que las notificaciones marcadas como leídas no se muestren
                this.loadNotifications();
                this.loadUnreadCount();
            }),
            catchError(error => {
                console.error('Error marcando todas como leídas:', error);
                return of(null);
            })
        );
    }

    /**
     * Obtiene el observable de notificaciones
     */
    getNotifications(): Observable<NotificationData[]> {
        return this.notifications$.asObservable();
    }

    /**
     * Obtiene el observable del conteo de no leídas
     */
    getUnreadCount(): Observable<number> {
        return this.unreadCount$.asObservable();
    }

    /**
     * Obtiene el observable del estado de carga
     */
    getIsLoading(): Observable<boolean> {
        return this.isLoading$.asObservable();
    }

    /**
     * Recarga las notificaciones manualmente
     */
    refresh(): void {
        this.loadNotifications();
        this.loadUnreadCount();
    }

    ngOnDestroy(): void {
        this.stopPolling();
        this.destroying$.next();
        this.destroying$.complete();
    }
}

